import { useCallback, useEffect, useState } from 'react';
import { AcademicGoals, GoalAnalysis, api } from '../../lib/api';

const STATUS = {
  ok: { label: '已满足', mark: '成' },
  gap: { label: '有差距', mark: '进' },
  unknown: { label: '待补数据', mark: '问' },
  pending: { label: '待政策', mark: '候' },
};

function display(value: unknown) {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? '是' : '否';
  return String(value);
}

export function GoalPlanner({ writeAllowed, onSaved }: { writeAllowed: boolean; onSaved: () => void }) {
  const [goals, setGoals] = useState({ targetGpa: '', targetScholarship: '', note: '' });
  const [analysis, setAnalysis] = useState<GoalAnalysis>();
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [goalResponse, analysisResponse] = await Promise.all([
        api<{ data: AcademicGoals }>('/api/v1/profile/goals'),
        api<{ data: GoalAnalysis }>('/api/v1/profile/goals/analysis'),
      ]);
      const current = goalResponse.data || {};
      setGoals({ targetGpa: current.targetGpa?.toString() || '', targetScholarship: current.targetScholarship || '', note: current.note || '' });
      setAnalysis(analysisResponse.data);
    } catch (error) { setMessage(error instanceof Error ? error.message : '目标分析加载失败'); }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const save = async () => {
    setSaving(true); setMessage('');
    try {
      const targetGpa = goals.targetGpa.trim() ? Number(goals.targetGpa) : null;
      if (targetGpa !== null && (Number.isNaN(targetGpa) || targetGpa < 0 || targetGpa > 5)) throw new Error('目标学分绩应在 0–5 之间。');
      await api('/api/v1/profile/goals', { method: 'PUT', body: JSON.stringify({ targetGpa, targetScholarship: goals.targetScholarship.trim() || null, note: goals.note.trim() || null }) });
      setMessage('目标已保存，分析已按最新画像重算。');
      await load(); onSaved();
    } catch (error) { setMessage(error instanceof Error ? error.message : '目标保存失败'); }
    finally { setSaving(false); }
  };

  return <section className="paper-card goal-planner" id="goal-planner">
    <header><div><small>GOAL · EVIDENCE</small><h3>目标与差距</h3><p>先设方向，再用真实数据判断当前距离；缺失项会明确标成“待补数据”。</p></div><span>目的导向</span></header>
    <div className="goal-setting">
      <label>目标学分绩<input inputMode="decimal" disabled={!writeAllowed || saving} value={goals.targetGpa} placeholder="如 4.50" onChange={(event) => setGoals({ ...goals, targetGpa: event.target.value })} /></label>
      <label>目标奖项<input list="scholarship-options" disabled={!writeAllowed || saving} value={goals.targetScholarship} placeholder="如 国家奖学金" onChange={(event) => setGoals({ ...goals, targetScholarship: event.target.value })} /><datalist id="scholarship-options"><option value="国家奖学金" /><option value="人民奖学金" /><option value="优秀学生" /></datalist></label>
      <label className="goal-note">阶段备注<input disabled={!writeAllowed || saving} value={goals.note} placeholder="这一阶段最重要的取舍" onChange={(event) => setGoals({ ...goals, note: event.target.value })} /></label>
      <button className="primary-button" disabled={!writeAllowed || saving} onClick={() => void save()}>保存并重算</button>
    </div>
    {message ? <p className="goal-message" role="status">{message}</p> : null}
    <div className="goal-list">
      {(analysis?.goals || []).map((goal) => {
        const state = STATUS[goal.status] || STATUS.unknown;
        return <details className={`goal-item status-${goal.status}`} key={goal.key} open={goal.status === 'gap'}>
          <summary><i>{state.mark}</i><span><b>{goal.name}</b><small>{goal.category || '成长目标'}</small></span><em>{state.label}</em></summary>
          <div className="goal-evidence">{goal.criteria?.length ? goal.criteria.map((criterion) => <p key={criterion.label} className={`criterion-${criterion.status}`}><span>{criterion.label}</span><b>{display(criterion.current)}</b><small>目标 {display(criterion.required)}</small></p>) : <p className="goal-empty">暂无可比对条件</p>}</div>
          {goal.advice ? <footer>{Array.isArray(goal.advice) ? goal.advice.join('；') : goal.advice}</footer> : null}
        </details>;
      })}
      {!analysis && !message ? <p className="goal-empty">正在沿画像根脉计算目标差距…</p> : null}
    </div>
  </section>;
}
